import { LoadingOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/i18n/format';
import styles from './load-more-rows.module.scss';

interface LoadMoreRowsProps {
  remaining: number;
  /** calls_count_relation === 'gte' means the count is a floor, not a total. */
  isLowerBound?: boolean;
  atCeiling?: boolean;
  /** Row cap the API clamps at, owned by the caller so the two cannot drift apart. */
  ceiling: number;
  /** The rows already on screen stay put while the bigger request runs, so the button is
   *  the only thing that can report it. */
  isLoading?: boolean;
  onClick: () => void;
  className?: string;
}

/**
 * Row loader for the conversations table, replacing numbered pagination. Not
 * show-more-button, which truncates prose and takes {text, maxChars}.
 */
export default function LoadMoreRows({
  remaining,
  isLowerBound = false,
  atCeiling = false,
  ceiling,
  isLoading = false,
  onClick,
  className,
}: LoadMoreRowsProps) {
  const { t } = useTranslation('common');

  // The API clamps silently, so state the ceiling rather than keep offering more.
  if (atCeiling) {
    return (
      <p className={clsx(styles.ceiling, className)}>
        {t('loadMore.ceiling', { count: ceiling, shown: formatNumber(ceiling) })}
      </p>
    );
  }

  return (
    <button
      type="button"
      className={clsx(styles.button, className)}
      // Without this a second click bumps the page again and loads two pages at once.
      disabled={isLoading}
      aria-busy={isLoading}
      onClick={onClick}
    >
      {isLoading && <LoadingOutlined className={styles.spinner} spin />}
      {isLoading
        ? t('states.loading')
        : t('loadMore.remaining', {
            remaining: `${formatNumber(remaining)}${isLowerBound ? '+' : ''}`,
          })}
    </button>
  );
}
